<template>
  <div :class="['template-field', { open: selected, 'is-selected': isSelected }]"
       @click="onFieldClick()">
    <div v-if="data">
      <div class="d-flex align-items-center wrapper">
          <!-- checkbox of selection (just in selection mode) -->
        <div v-if="selectMode" class="select-box mr-1">
          <input type="checkbox" :checked="isSelected" @click.stop="onToggleSelect" />
        </div>

        <div class="handle">
          <feather-icon size="16" icon="MenuIcon" />
        </div>

        <div class="flex-fill field-name position-relative">
          <!-- question title input -->
          <input
            :class="['form-control', { 'border-danger': data.errors && data.errors.hasErrors }]"
            type="text"
            :placeholder="$t('risk.template.name')"
            v-model="data.name"
          />
          <!-- inline validation messages -->
          <div style="position: absolute; bottom: -28px; left: 75px">
            <span v-if="data.errors && data.errors.missingName" class="text-danger">
              {{ $t("risk.form_validations.name") }}
            </span>
            <span
              v-if="data.errors && data.errors.missingName && data.errors.missingType"
              class="mx-1 text-danger"
            >
              |
            </span>
            <span v-if="data.errors && data.errors.missingType" class="text-danger">
              {{ $t("risk.form_validations.type") }}
            </span>
          </div>
        </div>

        <!-- type/config opener -->
        <div class="d-flex field-config">
          <div class="flex-fill field-config-options">
            {{ $t(fieldName) }}
          </div>
          <span class="show-options-icon" @click.stop="showModal">
            <feather-icon size="16" icon="SettingsIcon" />
          </span>
        </div>

        <!-- delete button (only when selected) -->
        <div class="btn-remove" v-if="selected" @click.stop="$emit('remove')">
          <feather-icon size="16" icon="TrashIcon" />
        </div>
      </div>

      <!-- options panel -->
      <div class="field-options">
        <!-- required checkbox -->
        <div class="form-group form-check">
          <input
            type="checkbox"
            class="form-check-input"
            :id="`check-req-${data.item_id}`"
            v-model="data.options.required"
          />
          <label class="form-check-label" :for="`check-req-${data.item_id}`">
            {{ $t("risk.required") }}
          </label>
        </div>

        <!-- alarms checkbox -->
        <div class="form-group form-check">
          <input
            type="checkbox"
            class="form-check-input"
            :id="`check-alarm-${data.item_id}`"
            v-model="data.options.alarms"
          />
          <label class="form-check-label" :for="`check-alarm-${data.item_id}`">
            {{ $t("alarm.alarms") }}
          </label>
        </div>

        <!-- options preview -->
        <div v-if="data.options.fieldTypeCfg && data.options.fieldTypeCfg.data.length">
          <h6>{{ $t("risk.template.options") }}:</h6>
          <ul>
            <li v-for="(option, index) in data.options.fieldTypeCfg.data" :key="index">
              {{ option.name }}
              <span v-if="option.riskPercentage">- {{ option.riskPercentage }}%</span>
              <span v-if="option.allowComment"> — 💬</span>
            </li>
          </ul>
        </div>

        <!-- visibility Section -->
        <div class="mt-2 p-2 bg-white rounded border">
          <div class="d-flex align-items-center justify-content-between">
            <strong>Visibilidad</strong>

            <!-- switch(check) visibility-->
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`vis-toggle-${data.item_id}`"
                v-model="visibilityEnabled"
              />
              <label class="form-check-label small" :for="`vis-toggle-${data.item_id}`">
                Condicionar
              </label>
            </div>
          </div>

          <template v-if="visibilityEnabled">
            <!-- we save show_if[0] to keep compatibility-->
            <div class="d-flex align-items-center mt-2 flex-wrap">
              <!-- select previous question (same section) -->
              <select
                class="form-control form-control-sm mr-2 mb-2"
                v-model="singleCond.field"
                style="min-width: 240px"
                @change="onRefChange"
              >
                <option disabled value="">Pregunta (previa)...</option>
                <option v-for="f in availableRefs" :key="f.item_id" :value="f.item_id">
                  {{ f.name || '(' + f.item_id.slice(0,6) + ')' }}
                </option>
              </select>

              <!-- If ref is checkbox/multi, show coincidence mode -->
              <select
                v-if="isRefMulti"
                class="form-control form-control-sm mr-2 mb-2"
                v-model="matchMode"
                style="min-width: 160px"
              >
                <option value="any">Coincida alguna</option>
                <option value="all">Coincidan todas</option>
              </select>

              <!-- Values -->
              <div class="flex-fill mb-2" v-if="needsValue">
                <!-- with catalogue -->
                <template v-if="refOptions.length">
                  <!-- multi -->
                  <select
                    v-if="isRefMulti"
                    class="form-control form-control-sm"
                    v-model="multiValue"
                    multiple
                  >
                    <option v-for="opt in refOptions" :key="String(opt.value)" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>
                  <!-- hint -->
                  <div
                    v-if="isRefMulti"
                    class="small mt-1"
                    :class="data.errors && data.errors.missingCondValue ? 'text-danger' : 'text-muted'"
                  >
                    Selecciona una o más opciones (Ctrl/Cmd + click).
                  </div>

                  <!-- single -->
                  <select
                    v-else
                    class="form-control form-control-sm"
                    v-model="singleValue"
                  >
                    <option disabled value="">Selecciona...</option>
                    <option v-for="opt in refOptions" :key="String(opt.value)" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>
                </template>

                <!-- fallback without catalogue -->
                <template v-else>
                  <input
                    class="form-control form-control-sm"
                    :type="valueInputType"
                    v-model="singleValue"
                    :placeholder="valuePlaceholder"
                  />
                </template>
              </div>

              <!-- clear -->
              <button
                type="button"
                class="btn btn-link text-danger btn-sm ml-2 mb-2"
                @click.stop="clearVisibility"
              >
                Limpiar
              </button>
            </div>

            <!-- errors -->
            <div v-if="data.errors && (data.errors.invalidConditions || data.errors.invalidOrder || data.errors.missingCondValue)" class="text-danger small mt-2">
              <div v-if="data.errors.invalidConditions">La pregunta referenciada ya no existe en esta sección.</div>
              <div v-if="data.errors.invalidOrder">La pregunta referenciada debe ser anterior.</div>
              <div v-if="data.errors.missingCondValue">Selecciona al menos una opción para la condición.</div>
            </div>
          </template>

          <template v-else>
            <div class="text-muted small mt-1">
              Sin condiciones: este campo siempre es visible.
            </div>
          </template>
        </div>
        <!-- /Visibility section -->
      </div>
      <!-- options panel -->
    </div>
  </div>
</template>

<script>
// imports the field structure defaults and available field types
import { fieldStructure, fieldTypes } from "./config";

export default {
  props: {
    // full field object from builder
    data: { type: Object, required: true },
    // whether this card is expanded/open
    selected: { type: Boolean, default: false },

    sectionFields: { type: Array, default: () => [] },
    indexInSection: { type: Number, default: 0 },

    //multiple selection
    selectMode: { type: Boolean, default: false },
    isSelected: { type: Boolean, default: false },
  },
  data() {
    return {
      // local copy only for id templates in inputs (kept for compatibility)
      fieldData: JSON.parse(JSON.stringify(fieldStructure)),
      _visGuard: false, // flag to avoid infinite loops
    };
  },
  computed: {
    // resolves the i18n key for the field type name
    fieldName() {
      const found = fieldTypes.find(
        (fType) => fType.type === (this.data && this.data.type)
      );
      return found ? found.name : "";
    },

    // Direct Visibility wiring
    sectionSignature() {
      const fields = this.sectionFields || [];
      const idx = Math.min(Math.max(this.indexInSection, 0), fields.length);
      const prev = fields.slice(0, idx);
      const sig = prev.map(f => ({
        id: f.item_id,
        type: f.type,
        opts: (((f.options || {}).fieldTypeCfg || {}).data || [])
          .map(o => (typeof o.value !== 'undefined' ? o.value : o.name))
      }));
      return JSON.stringify(sig);
    },

    visibilityEnabled: {
      get() { return Array.isArray(this.data.show_if) && this.data.show_if.length > 0; },
      set(v) {
        if (v) {
          if (!Array.isArray(this.data.show_if) || this.data.show_if.length === 0) {
            this.$set(this.data, 'show_if', [ { field: "", op: "==", value: "" } ]);
          } else if (this.data.show_if.length > 1) {
            this.$set(this.data, 'show_if', [ this.data.show_if[0] ]);
          }
          if (!this.data.show_logic) this.$set(this.data, 'show_logic', 'all');
        } else {
          this.$set(this.data, 'show_if', []);
        }
      }
    },

    singleCond: {
      get() {
        if (!Array.isArray(this.data.show_if) || this.data.show_if.length === 0) {
          return { field: "", op: "==", value: "" };
        }
        return this.data.show_if[0];
      },
      set(c) { this.$set(this.data, 'show_if', [ c ]); }
    },

    availableRefs() {
      const fields = this.sectionFields || [];
      const idx = Math.min(Math.max(this.indexInSection, 0), fields.length);
      return fields.slice(0, idx);
    },

    refField() {
      return (this.availableRefs || []).find(f => f.item_id === this.singleCond.field);
    },

    isRefMulti() {
      const t = (this.refField && this.refField.type || '').toLowerCase();
      return t.includes('checkbox') || t.includes('multi');
    },

    refOptions() {
      const rf = this.refField;
      if (!rf || !rf.options || !rf.options.fieldTypeCfg) return [];
      const data = rf.options.fieldTypeCfg.data || [];
      return data.map((d, idx) => ({
        label: d.name ?? String(d.value ?? idx),
        value: (typeof d.value !== 'undefined') ? d.value : (d.name ?? String(idx)),
      }));
    },

    singleValue: {
      get() { return this.singleCond.value; },
      set(v) {
        const c = { ...this.singleCond, op: '==', value: v };
        this.singleCond = c;
      }
    },

    multiValue: {
      get() { return Array.isArray(this.singleCond.value) ? this.singleCond.value : []; },
      set(arr) {
        const op = (this.matchMode === 'all') ? 'includes_all' : 'in';
        const c = { ...this.singleCond, op, value: Array.isArray(arr) ? arr : [] };
        this.singleCond = c;
      }
    },

    matchMode: {
      get() { return (this.singleCond.op === 'includes_all') ? 'all' : 'any'; },
      set(m) {
        const op = (m === 'all') ? 'includes_all' : 'in';
        const c = { ...this.singleCond, op };
        this.singleCond = c;
      }
    },

    needsValue() { return !!this.refField; },
    valueInputType() { return 'text'; },
    valuePlaceholder() { return 'Valor...'; },
  },
  mounted() {
    if (!this.data.options) this.$set(this.data, "options", {});
    if (!this.data.options.fieldTypeCfg) this.$set(this.data.options, "fieldTypeCfg", { data: [] });
    if (typeof this.data.options.alarms === "undefined") this.$set(this.data.options, "alarms", false);
    if (typeof this.data.options.required === "undefined") this.$set(this.data.options, "required", false);
    if (!this.data.errors) this.$set(this.data, "errors", {});
    if (!Array.isArray(this.data.show_if)) this.$set(this.data, "show_if", []);
    if (!this.data.show_logic) this.$set(this.data, "show_logic", "all");

    this.validateVisibility();
  },
  watch: {
    sectionSignature() { this.safeOnSectionFieldsChanged(); },
    indexInSection() { this.safeValidateVisibility(); },
    'data.show_if': { deep: true, handler() { this.safeValidateVisibility(); } },
  },
  methods: {
    onFieldClick() { this.$emit("item-selected", this.data); },
    showModal() { this.$emit("show-modal", this.data); },

    onToggleSelect() {
      this.$emit('toggle-select');
    },

    // safety wrappers
    safeOnSectionFieldsChanged() {
      if (this._visGuard) return;
      this._visGuard = true;
      try { this.onSectionFieldsChanged(); }
      finally { this.$nextTick(() => { this._visGuard = false; }); }
    },
    safeValidateVisibility() {
      if (this._visGuard) return;
      this._visGuard = true;
      try { this.validateVisibility(); }
      finally { this.$nextTick(() => { this._visGuard = false; }); }
    },

    onRefChange() {
      if (!this.refField) { this.clearVisibility(); return; }
      if (this.isRefMulti) {
        const first = this.refOptions[0] ? [this.refOptions[0].value] : [];
        this.multiValue = first;
        this.matchMode = 'any';
      } else {
        const first = this.refOptions[0] ? this.refOptions[0].value : '';
        this.singleValue = first;
      }
      this.validateVisibility();
    },

    clearVisibility() {
      this.$set(this.data, 'show_if', []);
      this.$set(this.data, 'show_logic', 'all');
      this.validateVisibility();
    },

    onSectionFieldsChanged() {
      if (!this.refField || !this.availableRefs.find(f => f.item_id === this.singleCond.field)) {
        this.clearVisibility();
        return;
      }
      if (this.isRefMulti) {
        const validSet = new Set(this.refOptions.map(o => o.value));
        const curr = Array.isArray(this.singleCond.value) ? this.singleCond.value : [];
        const next = curr.filter(v => validSet.has(v));
        if (next.length !== curr.length || next.some((v,i) => v !== curr[i])) this.multiValue = next;
      } else {
        const validSet = new Set(this.refOptions.map(o => o.value));
        const curr = this.singleCond.value;
        if (!validSet.has(curr)) {
          const first = this.refOptions[0] ? this.refOptions[0].value : '';
          if (curr !== first) this.singleValue = first;
        }
      }
      this.validateVisibility();
    },

    // visibility validation / error flags
    validateVisibility() {
      const fields = this.sectionFields || [];
      const idToIndex = new Map(fields.map((f, idx) => [f.item_id, idx]));
      const idxSelf = this.indexInSection;
      const cond = this.singleCond;

      let invalidConditions = false;
      let invalidOrder = false;
      let missingCondValue = false;

      if (this.visibilityEnabled) {
        const refIdx = (cond && cond.field && idToIndex.has(cond.field)) ? idToIndex.get(cond.field) : -1;
        if (refIdx === -1) invalidConditions = true;
        else if (refIdx >= idxSelf) invalidOrder = true;

        if (!invalidConditions && !invalidOrder && this.isRefMulti) {
          const arr = Array.isArray(cond.value) ? cond.value : [];
          if (arr.length === 0) missingCondValue = true;
        }
      }

      this.$set(this.data, 'errors', this.data.errors || {});
      this.$set(this.data.errors, 'invalidConditions', invalidConditions);
      this.$set(this.data.errors, 'invalidOrder', invalidOrder);
      this.$set(this.data.errors, 'missingCondValue', missingCondValue);

      const base = !!(this.data.errors.missingName || this.data.errors.missingType);
      this.$set(this.data.errors, 'hasErrors', base || invalidConditions || invalidOrder || missingCondValue);
    },
  },
};
</script>

<style lang="scss" scoped>
.template-field {
  padding: 0 8px;
  height: 50px;
  overflow: hidden;
  background-color: #f4f4f4;
  border-bottom: 1px solid #d1d1d1;
  &:last-child { border: none; }
  &.open { height: auto; }
}
.handle { cursor: move; margin-right: 5px; }
.field-name { padding: 5px 10px; border-right: 1px solid #d1d1d1; }
.field-config { margin-left: 10px; width: 300px; .show-options-icon { cursor: pointer; } }
.field-options { margin-top: 2px; margin-left: -10px; margin-right: -10px; background-color: #e5e5e5; padding: 5px 10px 10px; }
.btn-remove { position: absolute; right: -10px; cursor: pointer; }

/* visual selection */
.select-box input { cursor: pointer; }
.template-field.is-selected { outline: 2px dashed #4e8cff; outline-offset: -2px; }

.field-options .form-check {
  display: block;
  margin-bottom: 6px;
}
.field-options .form-check-input { margin-right: 6px; }
.field-options .form-check-label { vertical-align: middle; }
</style>
